import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { FlightFrame } from '../types';

/**
 * Shot tracer — colored line that fades to transparent at the tail.
 * Supports multiple shots: old tracers are "frozen" and dimmed,
 * while the active shot tracer stays bright.
 */
export class ShotTracer {
  private scene: Scene;
  private mesh: Mesh | null = null;
  private frozenMeshes: Mesh[] = [];
  private visible = true;
  private color = { r: 1, g: 1, b: 1 };
  /** Cache the last points used in buildFromFrames so we can rebuild for recolor. */
  private lastPoints: Vector3[] | null = null;
  /** Track frame count to skip redundant rebuilds (iOS perf critical). */
  private lastBuildCount = 0;
  private selectedBand: Mesh | null = null;
  private selectedMaterial: StandardMaterial | null = null;
  private selected = false;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /** Set the tracer color for the next shot (default: white). */
  setColor(r: number, g: number, b: number): void {
    this.color = { r, g, b };
  }

  /** Reset tracer color back to white. */
  resetColor(): void {
    this.color = { r: 1, g: 1, b: 1 };
  }

  buildFromFrames(frames: FlightFrame[], upToIndex?: number): void {
    const count = upToIndex !== undefined ? Math.min(upToIndex + 1, frames.length) : frames.length;
    if (count < 2) return;

    // Skip rebuild if frame count hasn't changed — avoids 300 mesh create/dispose
    // cycles per second during comparison mode (5 shots × 60fps) which causes
    // WebGL context loss on iOS Safari.
    if (count === this.lastBuildCount && this.mesh) return;
    this.lastBuildCount = count;

    const points: Vector3[] = [];
    const colors: Color4[] = [];

    for (let i = 0; i < count; i++) {
      const f = frames[i];
      points.push(new Vector3(f.position.x, f.position.y, f.position.z));

      // Color with alpha gradient — bright at head, fading at tail
      const progress = count > 1 ? i / (count - 1) : 1;
      const alpha = 0.65 + progress * 0.35;
      colors.push(new Color4(this.color.r, this.color.g, this.color.b, alpha));
    }

    // Cache points for recolor support
    this.lastPoints = points;

    // Create new mesh BEFORE disposing old one to prevent flicker
    const newMesh = MeshBuilder.CreateLines('shotTracer', {
      points,
      colors,
      updatable: false,
    }, this.scene);
    newMesh.isVisible = this.visible;

    // Now dispose old mesh
    if (this.mesh) {
      this.mesh.dispose();
    }
    this.mesh = newMesh;
    if (this.selected) this.rebuildSelectedBand();
  }

  /**
   * Freeze the current active tracer as history.
   * Dims it and moves it to the frozen array so a new shot can begin.
   * @param alpha — frozen opacity (default 0.25, use higher for comparison mode)
   */
  freeze(alpha = 0.25): void {
    if (!this.mesh) return;

    // Dim old shot tracer — use alpha/visibility for LinesMesh
    (this.mesh as any).alpha = alpha;

    this.frozenMeshes.push(this.mesh);
    this.mesh = null;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    if (this.mesh) {
      this.mesh.isVisible = visible;
    }
    // Toggle frozen tracers too
    for (const m of this.frozenMeshes) {
      m.isVisible = visible;
    }
    if (this.selectedBand) this.selectedBand.isVisible = visible;
  }

  isVisible(): boolean {
    return this.visible;
  }

  toggle(): boolean {
    this.setVisible(!this.visible);
    return this.visible;
  }

  /** A cross-platform thick highlight; WebGL lineWidth is not reliable on phones. */
  setSelected(selected: boolean): void {
    this.selected = selected;
    if (selected) this.rebuildSelectedBand();
    else this.clearSelectedBand();
  }

  private rebuildSelectedBand(): void {
    this.clearSelectedBand();
    if (!this.lastPoints || this.lastPoints.length < 2) return;
    this.selectedMaterial = new StandardMaterial('selectedFlightBandMaterial', this.scene);
    this.selectedMaterial.disableLighting = true;
    this.selectedMaterial.emissiveColor = new Color3(this.color.r, this.color.g, this.color.b);
    this.selectedMaterial.diffuseColor = this.selectedMaterial.emissiveColor;
    this.selectedMaterial.alpha = .9;
    this.selectedMaterial.backFaceCulling = false;
    this.selectedBand = MeshBuilder.CreateTube('selectedFlightBand', {
      path: this.lastPoints,
      radius: .62,
      tessellation: 6,
      cap: Mesh.NO_CAP,
      updatable: false,
    }, this.scene);
    this.selectedBand.material = this.selectedMaterial;
    this.selectedBand.isPickable = false;
    this.selectedBand.isVisible = this.visible;
  }

  private clearSelectedBand(): void {
    this.selectedBand?.dispose();
    this.selectedMaterial?.dispose();
    this.selectedBand = null;
    this.selectedMaterial = null;
  }

  /** Set the overall opacity for this tracer (active + frozen meshes). */
  setAlpha(alpha: number): void {
    if (this.mesh) {
      (this.mesh as any).alpha = alpha;
    }
    for (const m of this.frozenMeshes) {
      (m as any).alpha = alpha;
    }
  }

  /**
   * Recolor the existing tracer mesh in-place by rebuilding with new vertex colors.
   * Works even on LinesMesh where .alpha is unreliable due to useVertexAlpha.
   */
  recolor(r: number, g: number, b: number): void {
    this.color = { r, g, b };

    // Rebuild the active mesh with new colors
    if (this.mesh && this.lastPoints && this.lastPoints.length >= 2) {
      const points = this.lastPoints;
      const colors: Color4[] = [];
      for (let i = 0; i < points.length; i++) {
        const progress = points.length > 1 ? i / (points.length - 1) : 1;
        const alpha = 0.65 + progress * 0.35;
        colors.push(new Color4(r, g, b, alpha));
      }

      const newMesh = MeshBuilder.CreateLines('shotTracer', {
        points,
        colors,
        updatable: false,
      }, this.scene);
      newMesh.isVisible = this.visible;

      this.mesh.dispose();
      this.mesh = newMesh;
    }

    // Rebuild all frozen meshes too
    // Frozen meshes don't have cached points, so we extract from vertex buffer
    // For simplicity, frozen meshes get a flat color at reduced alpha
    for (let fi = 0; fi < this.frozenMeshes.length; fi++) {
      const fm = this.frozenMeshes[fi];
      const posData = fm.getVerticesData('position');
      if (!posData || posData.length < 6) continue;

      const numVerts = posData.length / 3;
      const pts: Vector3[] = [];
      const cols: Color4[] = [];
      for (let i = 0; i < numVerts; i++) {
        pts.push(new Vector3(posData[i * 3], posData[i * 3 + 1], posData[i * 3 + 2]));
        const progress = numVerts > 1 ? i / (numVerts - 1) : 1;
        const alpha = 0.10 + progress * 0.50;
        cols.push(new Color4(r, g, b, alpha));
      }

      const newFrozen = MeshBuilder.CreateLines('shotTracerFrozen', {
        points: pts,
        colors: cols,
        updatable: false,
      }, this.scene);
      newFrozen.isVisible = this.visible;

      fm.dispose();
      this.frozenMeshes[fi] = newFrozen;
    }
  }

  /**
   * Dispose only the active tracer (for mid-shot rebuilds).
   * Frozen history is kept.
   */
  dispose(): void {
    this.clearSelectedBand();
    if (this.mesh) {
      this.mesh.dispose();
      this.mesh = null;
    }
  }

  /**
   * Clear everything — active tracer and all history.
   */
  clearAll(): void {
    this.dispose();
    for (const m of this.frozenMeshes) {
      m.dispose();
    }
    this.frozenMeshes = [];
    this.lastBuildCount = 0;
  }
}
